import { supabase } from '../lib/supabase'

export const courseService = {
  // Get all courses for current user
  async getCourses() {
    try {
      const { data, error } = await supabase
        .from('courses_clomapper')
        .select('*')
        .order('created_at', { ascending: false })
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Get course creators (to display who created each course)
  async getCourseCreators() {
    try {
      // First get user IDs from courses
      const { data: coursesData, error: coursesError } = await supabase
        .from('courses_clomapper')
        .select('id, user_id')
      
      if (coursesError) throw coursesError
      
      // Create a set of unique user IDs to fetch
      const userIds = [...new Set(coursesData
        .filter(course => course.user_id)
        .map(course => course.user_id))];
      
      // If no user IDs found, return early
      if (userIds.length === 0) {
        return { 
          data: coursesData.map(course => ({
            course_id: course.id,
            user_id: course.user_id,
            email: 'Unknown',
            user_name: 'Unknown'
          })),
          error: null 
        };
      }
      
      // Get user data from auth.users table
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, email, raw_user_meta_data')
        .in('id', userIds);
      
      if (usersError) {
        console.error("Error fetching users:", usersError);
        // Fall back to returning courses with unknown users
        return { 
          data: coursesData.map(course => ({
            course_id: course.id,
            user_id: course.user_id,
            email: 'Unknown',
            user_name: 'Unknown'
          })),
          error: null 
        };
      }
      
      // Create a map of user_id to user data for quick lookup
      const userMap = {};
      usersData.forEach(user => {
        userMap[user.id] = user;
      });
      
      // Format the data to extract user names
      const formattedData = coursesData.map(course => {
        let userName = 'Unknown';
        let email = 'Unknown';
        
        // Look up the user in our map
        const user = userMap[course.user_id];
        
        if (user) {
          // Try to get name from user metadata
          if (user.raw_user_meta_data && user.raw_user_meta_data.name) {
            userName = user.raw_user_meta_data.name;
          } else {
            // Fallback to email
            email = user.email || 'Unknown';
            userName = email;
            
            // If email exists, only show part before @ symbol
            if (userName && userName.includes('@')) {
              userName = userName.split('@')[0];
            }
          }
        }
        
        return {
          course_id: course.id,
          user_id: course.user_id,
          email: email,
          user_name: userName
        };
      });
      
      return { data: formattedData, error: null };
    } catch (error) {
      console.error("Error in getCourseCreators:", error);
      return { data: null, error: error.message };
    }
  },

  // Create new course
  async createCourse(courseData) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')
      const { data, error } = await supabase
        .from('courses_clomapper')
        .insert([
          {
            user_id: user.id,
            course_name: courseData.courseName,
            course_code: courseData.courseCode,
            faculty: courseData.faculty,
            programme: courseData.programme,
            credit_hours: courseData.creditHours,
            semester: courseData.semester,
            session: courseData.session,
            status: courseData.status || 'draft',
            progress: courseData.progress || 0
          }
        ])
        .select()
        .single()
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Duplicate an existing course
  async duplicateCourse(courseId, newCourseName, creatorName) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')
      
      // First, get the original course data
      const { data: originalCourse, error: fetchError } = await supabase
        .from('courses_clomapper')
        .select('*')
        .eq('id', courseId)
        .single()
      
      if (fetchError) throw fetchError
      if (!originalCourse) throw new Error('Course not found')
      
      // Create a new course with the same data but a new name
      const { data: newCourse, error: createError } = await supabase
        .from('courses_clomapper')
        .insert([
          {
            user_id: user.id,
            course_name: newCourseName || `${originalCourse.course_name} (Copy)`,
            course_code: originalCourse.course_code,
            faculty: originalCourse.faculty,
            programme: originalCourse.programme,
            credit_hours: originalCourse.credit_hours,
            semester: originalCourse.semester,
            session: originalCourse.session,
            status: 'draft', // Always start as draft
            progress: originalCourse.progress
            // metadata column doesn't exist, so we don't include it
          }
        ])
        .select()
        .single()
      
      if (createError) throw createError
      
      // Now duplicate all the related data
      // 1. Copy CLO statements
      const { data: cloStatements, error: cloError } = await supabase
        .from('clo_statements_clomapper')
        .select('*')
        .eq('course_id', courseId)
      
      if (!cloError && cloStatements && cloStatements.length > 0) {
        const newCloStatements = cloStatements.map(clo => ({
          course_id: newCourse.id,
          clo_code: clo.clo_code,
          statement: clo.statement
        }))
        
        await supabase
          .from('clo_statements_clomapper')
          .insert(newCloStatements)
      }
      
      // 2. Copy PLO statements
      const { data: ploStatements, error: ploError } = await supabase
        .from('plo_statements_clomapper')
        .select('*')
        .eq('course_id', courseId)
      
      if (!ploError && ploStatements && ploStatements.length > 0) {
        const newPloStatements = ploStatements.map(plo => ({
          course_id: newCourse.id,
          plo_code: plo.plo_code,
          statement: plo.statement
        }))
        
        await supabase
          .from('plo_statements_clomapper')
          .insert(newPloStatements)
      }
      
      // 3. Copy CLO-PLO mappings
      const { data: cloPlomappings, error: mappingError } = await supabase
        .from('clo_plo_mapping_clomapper')
        .select('*')
        .eq('course_id', courseId)
      
      if (!mappingError && cloPlomappings && cloPlomappings.length > 0) {
        const newMappings = cloPlomappings.map(mapping => ({
          course_id: newCourse.id,
          clo_code: mapping.clo_code,
          plo_code: mapping.plo_code,
          is_mapped: mapping.is_mapped
        }))
        
        await supabase
          .from('clo_plo_mapping_clomapper')
          .insert(newMappings)
      }
      
      // 4. Copy assessment plan
      const { data: assessments, error: assessmentError } = await supabase
        .from('assessments_clomapper')
        .select('*')
        .eq('course_id', courseId)
      
      if (!assessmentError && assessments && assessments.length > 0) {
        const newAssessments = assessments.map(assessment => ({
          course_id: newCourse.id,
          assessment_name: assessment.assessment_name,
          assessment_type: assessment.assessment_type,
          marks: assessment.marks,
          clo_mapping: assessment.clo_mapping,
          slt_ci: assessment.slt_ci
        }))
        
        await supabase
          .from('assessments_clomapper')
          .insert(newAssessments)
      }
      
      // 5. Copy attainment targets
      const { data: targets, error: targetError } = await supabase
        .from('attainment_targets_clomapper')
        .select('*')
        .eq('course_id', courseId)
        .single()
      
      if (!targetError && targets) {
        await supabase
          .from('attainment_targets_clomapper')
          .insert({
            course_id: newCourse.id,
            clo_targets: targets.clo_targets,
            plo_targets: targets.plo_targets
          })
      }
      
      // 6. Copy SLT configuration if available
      const { data: sltConfig, error: sltError } = await supabase
        .from('slt_configuration_clomapper')
        .select('*')
        .eq('course_id', courseId)
        .single()
      
      if (!sltError && sltConfig) {
        await supabase
          .from('slt_configuration_clomapper')
          .insert({
            course_id: newCourse.id,
            course_slt_config: sltConfig.course_slt_config,
            teaching_slt_by_clo: sltConfig.teaching_slt_by_clo,
            breakdown_config: sltConfig.breakdown_config
          })
      }
      
      // If creator name was provided, try to store it in a separate table
      if (creatorName && creatorName.trim()) {
        try {
          // First check if the course_creators_clomapper table exists
          const { error: tableCheckError } = await supabase
            .from('course_creators_clomapper')
            .select('id')
            .limit(1);
          
          if (!tableCheckError) {
            // Table exists, so insert the creator info
            await supabase
              .from('course_creators_clomapper')
              .insert({
                course_id: newCourse.id,
                user_id: user.id,
                creator_name: creatorName.trim()
              });
          } else {
            console.log("course_creators_clomapper table doesn't exist, skipping creator name storage");
          }
        } catch (error) {
          console.error("Error storing creator name:", error);
          // Continue even if this fails as it's not critical
        }
      }
      
      return { data: newCourse, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Update course
  async updateCourse(courseId, updates) {
    try {
      const { data, error } = await supabase
        .from('courses_clomapper')
        .update(updates)
        .eq('id', courseId)
        .select()
        .single()
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Delete course - now handles both active and finalized courses
  async deleteCourse(courseId) {
    try {
      // First check if the course is finalized
      const { data: courseData, error: fetchError } = await supabase
        .from('courses_clomapper')
        .select('status')
        .eq('id', courseId)
        .single()
      
      if (fetchError) throw fetchError
      
      // Delete from archived_reports_clomapper if the course is finalized
      if (courseData.status === 'finalized') {
        try {
          // Try to delete from archived reports table if it exists
          await supabase
            .from('archived_reports_clomapper')
            .delete()
            .eq('course_id', courseId)
          console.log('Deleted finalized course from archived reports table');
        } catch (archiveError) {
          // This might fail if the table doesn't exist, but we continue
          console.log('Error deleting from archived reports, may not exist:', archiveError);
        }
      }
      
      // Delete from courses_clomapper
      const { error } = await supabase
        .from('courses_clomapper')
        .delete()
        .eq('id', courseId)
      
      if (error) throw error
      return { error: null }
    } catch (error) {
      return { error: error.message }
    }
  },

  // Get course by ID
  async getCourseById(courseId) {
    try {
      const { data, error } = await supabase
        .from('courses_clomapper')
        .select('*')
        .eq('id', courseId)
        .single()
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Update course progress
  async updateCourseProgress(courseId, progress) {
    try {
      const { data, error } = await supabase
        .from('courses_clomapper')
        .update({ progress })
        .eq('id', courseId)
        .select()
        .single()
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Complete course
  async completeCourse(courseId) {
    try {
      const { data, error } = await supabase
        .from('courses_clomapper')
        .update({ status: 'completed', completed_at: new Date().toISOString(), progress: 100 })
        .eq('id', courseId)
        .select()
        .single()
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Finalize course (new method)
  async finalizeCourse(courseId, completeReport) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      // First update the course status
      const { data: courseData, error: courseError } = await supabase
        .from('courses_clomapper')
        .update({
          status: 'finalized',
          finalized_at: new Date().toISOString(),
          complete_report: completeReport,
          progress: 100
        })
        .eq('id', courseId)
        .select()
        .single()
      
      if (courseError) throw courseError
      
      // Then save to archived_reports_clomapper if the table exists
      try {
        const { data: archivedData, error: archiveError } = await supabase
          .from('archived_reports_clomapper')
          .insert({
            user_id: user.id,
            course_id: courseId,
            course_name: courseData.course_name,
            course_code: courseData.course_code,
            faculty: courseData.faculty,
            programme: courseData.programme,
            semester: courseData.semester,
            session: courseData.session,
            completed_at: new Date().toISOString(),
            status: 'finalized',
            report: completeReport
          })
          .select()

        if (archiveError) {
          console.error("Error saving to archived reports:", archiveError);
          // Continue even if archive fails - the report is still in the courses table
        } else {
          console.log("Successfully saved to archived reports:", archivedData);
        }
      } catch (archiveError) {
        console.error("Exception when saving to archived reports:", archiveError);
        // Continue even if archive fails - the report is still in the courses table
      }
      
      return { data: courseData, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },
  
  // Get archived reports
  async getArchivedReports() {
    try {
      // First try to get from the dedicated archived_reports table if it exists
      let { data: archivedData, error: archivedError } = await supabase
        .from('archived_reports_clomapper')
        .select('*')
        .order('completed_at', { ascending: false })
      
      // If the table doesn't exist or there's an error, fall back to courses table
      if (archivedError) {
        console.log("Falling back to courses table for archived reports");
        const { data: courseData, error: courseError } = await supabase
          .from('courses_clomapper')
          .select('*')
          .in('status', ['finalized', 'archived'])
          .order('finalized_at', { ascending: false })
        
        if (courseError) throw courseError
        
        // Format course data to match archived reports format
        return { 
          data: courseData.map(course => ({
            id: course.id,
            courseId: course.id,
            courseName: course.course_name,
            courseCode: course.course_code,
            faculty: course.faculty,
            programme: course.programme,
            semester: course.semester,
            session: course.session,
            completedAt: course.finalized_at || course.completed_at,
            status: course.status,
            report: course.complete_report
          })), 
          error: null 
        }
      }
      
      // Format archived reports data to match expected format
      return { 
        data: archivedData.map(report => ({
          id: report.id,
          courseId: report.course_id,
          courseName: report.course_name,
          courseCode: report.course_code,
          faculty: report.faculty,
          programme: report.programme,
          semester: report.semester,
          session: report.session,
          completedAt: report.completed_at,
          status: report.status,
          report: report.report
        })),
        error: null 
      }
    } catch (error) {
      return { data: null, error: error.message }
    }
  }
}